
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import Booking, { TableAssignmentStatus } from "@/domain/booking/Booking";
import DialogBanner from "@/components/app/DialogBanner.vue";
import BookingViewDatePicker from "@/components/booking/components/BookingViewDatePicker.vue";
import BookingDetailsForm from "@/components/booking/components/BookingDetailsForm.vue";
import BookingOverview from "@/components/booking/components/BookingOverview.vue";
import TimeService from "@/services/TimeService";
import RemoteServices from "@/services/RemoteServices";
import BookingPropertiesPicker from "@/components/booking/components/BookingPropertiesPicker.vue";
import Room from "@/domain/restaurant/Room";
import Table from "@/domain/restaurant/Table";
import ConfirmationDialog from "@/components/alert/ConfirmationDialog.vue";
import NextButton from "@/components/app/NextButton.vue";
import Notice from "@/domain/restaurant/Notice";
import StatCard from "@/components/stats/StatCard.vue";
import NoticeCard from "@/components/restaurant/notice/NoticeCard.vue";
@Component({
  components: {
    NoticeCard,
    StatCard,
    NextButton,
    ConfirmationDialog,
    BookingPropertiesPicker,
    BookingOverview,
    BookingDetailsForm,
    BookingViewDatePicker,
    DialogBanner
  }
})
export default class NewBookingDialog extends Vue {
  @Prop({ default: false })
  value!: boolean;

  @Prop({ default: () => new Booking() })
  booking!: Booking;

  @Prop({ default: () => new Date() })
  parentSelectedDate!: Date;

  @Prop({ default: () => {} })
  tables!: { [key: number]: Table };

  @Prop()
  numberOfPeopleOnDate!:number

  @Prop({default: false})
  loadingBookings!: boolean;

  @Prop()
  notice!: Notice | null

  selectedTime: string = "";

  currentStep = 0;

  selectedRoom: Room | null = null;

  isBookingDetailsFormValid = false;

  isBookingPropertiesPickerValid = false;

  localSelectedDate: Date = new Date(this.parentSelectedDate.getTime());

  isRequestedTable: boolean = false;

  showTimePicker = false;

  selectedTable: Table | null = null;

  loading = false;

  tableAssignmentStatus: TableAssignmentStatus | null = null;

  get hideBannerActions() {
    return this.isOverviewTab;
  }

  setShowTimePicker(newVal: boolean) {
    this.showTimePicker = newVal;
  }

  get confirmationDialog() {
    return this.$refs.confirmationDialog as ConfirmationDialog;
  }

  get dialog() {
    return this.value;
  }

  set dialog(newValue) {
    this.$emit("input", newValue);
  }

  resetTableAssignmentStatus() {
    this.booking.tableAssignmentStatus = undefined;
    this.tableAssignmentStatus = null;
    this.setRequestedTableId(null);
  }

  setIsRequestedTable() {
    this.booking.setRequestedTable();
    this.tableAssignmentStatus = Booking.getTableAssignmentStatusFromString(
      this.booking.tableAssignmentStatus
    );
  }

  setIsVIP() {
    this.booking.setVIP();
    this.tableAssignmentStatus = Booking.getTableAssignmentStatusFromString(
      this.booking.tableAssignmentStatus
    );
  }

  get isPropertiesTab() {
    return this.currentStep === 0;
  }

  get isCustomerDetailsTab() {
    return this.currentStep === 1;
  }

  get isOverviewTab() {
    return this.currentStep === 2;
  }

  get bookingPropertiesPicker() {
    return this.$refs.BookingPropertiesPicker as BookingPropertiesPicker;
  }

  get assignedTable(): Table | null {
    if (this.booking.tableId != null) {
      return this.tables[this.booking.tableId];
    }
    return null;
  }

  setBookingDateTime() {
    this.currentStep = 1;
    TimeService.setDateHours(this.localSelectedDate, this.selectedTime);
    this.booking.bookingDateTime = TimeService.getISO8601DateTimeString(
      this.localSelectedDate
    );
  }

  setSelectedRoom(newRoom: Room) {
    this.selectedRoom = newRoom;
  }

  setRequestedTableId(newTableId: number | null) {
    if (newTableId == null) {
      this.booking.tableId = undefined;
      this.selectedTable = null;
    } else {
      this.booking.tableId = newTableId;
      this.selectedTable = this.tables[this.booking.tableId];
    }
  }

  updateTime(newTime: string) {
    this.selectedTime = newTime;
  }

  updateDate(newDate: Date) {
    this.localSelectedDate = newDate;
    this.emitDateChange(newDate);
  }

  getBannerTitle() {
    if (this.currentStep === 0)
      return this.$t("booking.create.dateAndNumberOfPeople");
    else if (this.currentStep === 1)
      return this.$t("booking.create.bookingDetails");
    else if (this.currentStep === 2) return this.$t("booking.create.overview");
  }

  async goBack() {
    if (this.loading) return;
    if (this.currentStep === 0) {
      if (
        await this.confirmationDialog.open(
          this.$tc("booking.dialog.close.title"),
          this.$tc("booking.dialog.close.message")
        )
      ) {
        this.closeAndResetDialog();
      }
    } else {
      this.currentStep -= 1;
    }
  }

  closeAndResetDialog() {
    this.dialog = false;
    // this.booking = new Booking();
    this.currentStep = 0;
    this.selectedTime = "";
    this.selectedRoom = null;
    this.tableAssignmentStatus = null;
    this.selectedTable = null;
    this.resetLocalDate();
    this.loading = false;
  }

  @Watch("parentSelectedDate")
  resetLocalDate() {
    this.localSelectedDate = new Date(this.parentSelectedDate.getTime());
  }

  async addToWaitingList() {
    if (
      await this.confirmationDialog.open(
        this.$tc("booking.waitingList.add.label"),
        this.$tc("booking.waitingList.add.message")
      )
    ) {
      try {
        if (
          this.isBookingPropertiesPickerValid &&
          this.isBookingDetailsFormValid
        ) {
          this.booking.restaurantId = await this.$store.getters
            .getSelectedRestaurant.restaurantId;
          const newBooking = await RemoteServices.addBookingToWaitingList(
            this.booking
          );
          this.addNewBooking(newBooking);
          await this.$store.dispatch(
            "success",
            this.$t("booking.waitingList.add.success")
          );
          this.closeAndResetDialog();
        }
      } catch (error) {
        await this.$store.dispatch("error", error.message);
      }
    }
  }

  async createBooking() {
    this.loading = true;
    if (this.isBookingPropertiesPickerValid && this.isBookingDetailsFormValid)
      try {
        this.booking.restaurantId = await this.$store.getters
          .getSelectedRestaurant.restaurantId;
        const newBooking = await RemoteServices.createBooking(this.booking);
        this.addNewBooking(newBooking);
        await this.$store.dispatch(
          "success",
          this.$t("booking.create.success")
        );
        this.currentStep = 2;
      } catch (error) {
        await this.$store.dispatch("error", error.message);
      }
    else console.log("Create Booking Failed, Booking Properties Invalid.");
    this.loading = false;
  }

  getTableName(tableId: number | null): string | null {
    if (tableId != null && this.tables[tableId] != null)
      return this.tables[tableId].tableName;
    return this.$tc("booking.table.not.selected");
  }

  addNewBooking(booking: Booking) {
    Booking.updateBooking(this.booking, booking);
    this.selectedTable = this.assignedTable;
    this.$emit("new-booking", booking);
  }

  emitDateChange(date: Date){
    this.$emit("date-change", date);
  }
}
